/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * CampaignInfo Card component
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.12.0
 */

import { useCallback, useMemo } from 'react';

import { connect, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import ArrowTooltip from '@/main/components/common/Tooltip/Tooltip';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { Button, LinkBlock } from '@components/common';
import { ELinkTarget } from '@components/common/ExternalLink/ExternalLink';
import { useTable } from '@components/Table/hooks/useTable';
import { IPageCampaignInfoTranslation } from '@modules/internationalization/types/pages/campaignInfo';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { IRootReducer } from '@reducers/rootReducer';
import { openExternalRoute } from '@routes/constants/externalRoutes';
import { IRouteParams } from '@routes/constants/routesConstants';
import { stringFormatParams } from '@utils/stringFormat';

import { makeSelectFeatureEnabled } from '../../PageAuth/selectors/authSelectors';
import {
    campaignInfoGetActivateTypeAction,
    campaignInfoLinkVisitedAction,
} from '../campaignInfoActions';
import { ICampaignInfoReducer } from '../campaignInfoReducer';
import { makeSelectCampaignInfoActions, makeSelectCampaignInfoGeneral, makeSelectCampaignMaxParticipantsForActivation } from '../campaignInfoSelectors';
import { TCampaignInfoInitStartPayload } from '../types/campaignInfoActionTypes';
import { ECampaignInfoActions, ECampaignInfoType } from '../types/campaignInfoTypes';
import CampaignInfoModalRemove from './CampaignInfoModalRemove';
import { CampaignInfoCardStyled } from './styled/CampaignInfoStyled';
import { mapCampaignInfoTypeToTableName } from './Table/CampaignInfoTableCells';

export interface ICampaignInfoCard {
    translationCampaignInfo: IPageCampaignInfoTranslation;
}

const CampaignInfoCard = (props: IConnectedProps & IDispatchProps & ICampaignInfoCard) => {
    const {
        actions,
        info,
        maxParticipantsForActivation,
        translationCampaignInfo,

        getActivateType,
        modalOpen,
        sendLinkVisitedFeedback,
    } = props;
    /*
    * -------------------------------------------
    *           Get table info
    * -------------------------------------------
    */

    const {
        basketCount,
    } = useTable(mapCampaignInfoTypeToTableName[info.type]);

    const { campaignId } = useParams<IRouteParams>();

    const isCampaignsImprovementsFeature = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CAMPAIGNS_IMPROVEMENT));

    /*
    * -------------------------------------------
    *           Components visibility
    * -------------------------------------------
    */

    const isTableVisible = useMemo(() =>
        actions.includes(ECampaignInfoActions.participantList), [actions]);

    const isSelfManage = useCallback(() =>
        actions.includes(ECampaignInfoActions.selfManage), [actions]);

    const isApplyBtnVisible = useCallback(() =>
        actions.includes(ECampaignInfoActions.apply), [actions]);

    const isRemoveBtnVisible = useCallback(() =>
        actions.includes(ECampaignInfoActions.remove), [actions]);

    const isInformCampaignType = useCallback(() =>
        !actions.includes(ECampaignInfoActions.participantList) && !actions.includes(ECampaignInfoActions.selfManage),
        [actions]);

    const isCampaignMaxParticipants = 
        isCampaignsImprovementsFeature && !!maxParticipantsForActivation && info.type === ECampaignInfoType.subscriber;

    const isApplyButtonDisabled = 
        (
            isCampaignMaxParticipants
            ? (basketCount ?? 0) > maxParticipantsForActivation 
            : false
        )
        || (!basketCount && isTableVisible)

    const validateError = 
        (
            isCampaignMaxParticipants
            && (basketCount ?? 0) > maxParticipantsForActivation
        )
        ? stringFormatParams(
            translationCampaignInfo.subscriber.validateErrors.selectSubscribers, 
            { maxParticipantsForActivation },
        )
        : null;

    /*
    * -------------------------------------------
    *           Event handlers
    * -------------------------------------------
    */

    const onRemoveBtnHandler = useCallback(() => {
        modalOpen({
            render: (p: IModalRenderProps) => <CampaignInfoModalRemove
                isInformCampaignType={isInformCampaignType()}
                type={info.type}
                onCloseModal={p.onCloseModal}
            />,
        });
    }, [modalOpen, isInformCampaignType, info.type]);

    const onLinkDetailsHandler = useCallback((): void => {
        sendLinkVisitedFeedback({ campaignId })
        if (info.pageUrl) {
            openExternalRoute(info.pageUrl, '_blank')
        }
    }, [campaignId, info.pageUrl, sendLinkVisitedFeedback]);

    return (
        <CampaignInfoCardStyled>
            {
                info.title && (
                    <div className="card__title">
                        {info.title}
                    </div>
                )
            }
            {
                info.imgUrl && (
                    <div className="card__image-container">
                        <img alt="Campaign" className="card__image"
                            data-name="card-image-container" src={info.imgUrl} />
                    </div>
                )
            }
            {
                info.description && (
                    <div
                        className="card__description"
                        dangerouslySetInnerHTML={{ __html: info.description }}
                    />
                )
            }
            {
                info.pageUrl && (
                    <LinkBlock
                        inner
                        isExternalUrl
                        className="card__details"
                        target={ELinkTarget.BLANK}
                        url={info.pageUrl}
                        onClick={onLinkDetailsHandler}
                        onWheelClick={onLinkDetailsHandler}
                    >
                        {translationCampaignInfo.common.card.linkDetails}
                    </LinkBlock>
                )
            }

            {
                !isSelfManage() && !!basketCount && (
                    <div className="card__participant-chosen">
                        {
                            stringFormatParams(translationCampaignInfo[info.type].card.participantChosen,
                                { number: basketCount },
                            )
                        }
                    </div>
                )
            }
            {
                (isApplyBtnVisible() || isRemoveBtnVisible()) && (
                    <div className="card__btn-container">
                        {
                            isApplyBtnVisible() && (
                                    <ArrowTooltip
                                        disableHoverListener={!isApplyButtonDisabled}
                                        message={validateError}
                                        placement={'top-start'}
                                    >
                                        <div>
                                            <Button
                                                dataName="campaign-info-apply-btn"
                                                disabled={isApplyButtonDisabled}
                                                kind="medium"
                                                onClick={getActivateType}
                                            >
                                                {info.text.btnApply}
                                            </Button>
                                        </div>
                                    </ArrowTooltip>                                
                            )
                        }
                        {
                            isRemoveBtnVisible() && (
                                <LinkBlock
                                    dataName="campaign-info-remove-btn"
                                    disabled={!basketCount && !isInformCampaignType() && isTableVisible}
                                    onClick={onRemoveBtnHandler}
                                >
                                    {translationCampaignInfo.common.card.btnRemove}
                                </LinkBlock>
                            )
                        }
                    </div>
                )
            }
        </CampaignInfoCardStyled>
    );
};

export type IConnectedProps = Pick<ICampaignInfoReducer, 'actions' | 'info' | 'maxParticipantsForActivation'>;

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    actions: makeSelectCampaignInfoActions(),
    info: makeSelectCampaignInfoGeneral(),
    maxParticipantsForActivation: makeSelectCampaignMaxParticipantsForActivation(),
});

export interface IDispatchProps {
    getActivateType: () => void;
    modalOpen: (payload: IModalOpenPayload) => void;
    sendLinkVisitedFeedback: (payload: TCampaignInfoInitStartPayload) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    getActivateType: () => dispatch(campaignInfoGetActivateTypeAction()),
    modalOpen: (payload) => dispatch(modalOpen(payload)),
    sendLinkVisitedFeedback: payload => dispatch(campaignInfoLinkVisitedAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignInfoCard);
